import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import NoteEntryCard from '../components/common/NoteEntryCard';
import AddCardPlaceholder from '../components/common/AddCardPlaceholder';
import Skeleton from '../components/common/Skeleton';
import ConfirmationModal from '../components/modals/ConfirmationModal';
import { useStoryboards } from '../hooks/useStoryboards';
import { X, Tag } from 'lucide-react';

/* ============================================================
   Animated dots: .  ..  ...
   ============================================================ */
const AnimatedDots = () => {
    const [count, setCount] = useState(1);
    useEffect(() => {
        const interval = setInterval(() => setCount(c => (c % 3) + 1), 400);
        return () => clearInterval(interval);
    }, []);
    return <span>{'.'.repeat(count)}</span>;
};

/* ============================================================
   TagInput — autocomplete combobox
   ============================================================ */
const TagInput = ({ tags, selectedTagIds, onChange, onCreateTag, isDarkMode, placeholder }) => {
    const [query, setQuery] = useState('');
    const [highlightIndex, setHighlightIndex] = useState(-1);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [creating, setCreating] = useState(false);
    const inputRef = useRef(null);
    const listRef = useRef(null);

    const canCreate = typeof onCreateTag === 'function';

    // Normalize IDs to Number for consistent comparison (PHP returns mixed string/int)
    const normalizedSelected = selectedTagIds.map(Number);

    // Tags not yet selected, filtered by query
    const suggestions = tags.filter(
        (t) => !normalizedSelected.includes(Number(t.id)) &&
            t.name.toLowerCase().includes(query.toLowerCase().trim())
    );

    // Does the query exactly match an existing tag name?
    const exactMatch = tags.find(
        (t) => t.name.toLowerCase() === query.trim().toLowerCase()
    );

    // Selected tag objects — preserve selection order (first selected = leftmost)
    const selectedTags = normalizedSelected.map(id => tags.find(t => Number(t.id) === id)).filter(Boolean);

    // Scroll highlighted item into view
    useEffect(() => {
        if (highlightIndex >= 0 && listRef.current) {
            const item = listRef.current.children[highlightIndex];
            item?.scrollIntoView({ block: 'nearest' });
        }
    }, [highlightIndex]);

    const handleSelect = useCallback((tagId) => {
        onChange([...selectedTagIds, Number(tagId)]);
        setQuery('');
        setHighlightIndex(-1);
        setShowSuggestions(false);
        inputRef.current?.focus();
    }, [selectedTagIds, onChange]);

    const handleRemove = useCallback((tagId) => {
        onChange(selectedTagIds.filter((id) => Number(id) !== Number(tagId)));
        inputRef.current?.focus();
    }, [selectedTagIds, onChange]);

    const handleCreateAndSelect = useCallback(async () => {
        const name = query.trim();
        if (!name || creating) return;
        setCreating(true);
        try {
            const result = await onCreateTag({ name });
            if (result) {
                onChange([...selectedTagIds, Number(result.id)]);
                setQuery('');
                setHighlightIndex(-1);
                setShowSuggestions(false);
                inputRef.current?.focus();
            }
        } finally {
            setCreating(false);
        }
    }, [query, selectedTagIds, onChange, onCreateTag, creating]);

    const handleKeyDown = (e) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            if (!showSuggestions) { setShowSuggestions(true); setHighlightIndex(0); return; }
            setHighlightIndex((prev) => Math.min(prev + 1, suggestions.length - 1));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setHighlightIndex((prev) => Math.max(prev - 1, 0));
        } else if (e.key === 'Enter') {
            e.preventDefault();
            e.stopPropagation();
            if (highlightIndex >= 0 && highlightIndex < suggestions.length) {
                // Select highlighted existing tag
                handleSelect(suggestions[highlightIndex].id);
            } else if (query.trim()) {
                if (exactMatch && !normalizedSelected.includes(Number(exactMatch.id))) {
                    // Exact match with existing tag — select it
                    handleSelect(exactMatch.id);
                } else if (!exactMatch && canCreate) {
                    // Create new tag
                    handleCreateAndSelect();
                }
            }
        } else if (e.key === 'Escape') {
            setShowSuggestions(false);
            setHighlightIndex(-1);
        } else if (e.key === 'Backspace' && query === '' && selectedTagIds.length > 0) {
            // Remove last tag
            handleRemove(selectedTagIds[selectedTagIds.length - 1]);
        }
    };

    const handleInputChange = (e) => {
        setQuery(e.target.value);
        setHighlightIndex(-1);
        setShowSuggestions(true);
    };

    const handleFocus = () => {
        if (query.trim() || suggestions.length > 0) {
            setShowSuggestions(true);
        }
    };

    const handleBlur = () => {
        // Delay to allow suggestion click to register
        setTimeout(() => setShowSuggestions(false), 150);
    };

    const showDropdown = showSuggestions && (suggestions.length > 0 || (canCreate && query.trim() && !exactMatch));

    return (
        <div className="relative">
            {/* Chips + Input inside a styled container */}
            <div
                className={`
                    flex flex-wrap items-center gap-1.5 px-2.5 py-2 rounded-lg border transition-colors min-h-[38px] cursor-text
                    ${isDarkMode
                        ? 'bg-slate-700/50 border-slate-600 focus-within:border-indigo-500'
                        : 'bg-slate-50 border-slate-200 focus-within:border-indigo-500'
                    }
                `}
                onClick={() => inputRef.current?.focus()}
            >
                {/* Selected tag chips */}
                {selectedTags.map((tag) => (
                    <span
                        key={tag.id}
                        className={`
                            inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-medium
                            ${isDarkMode
                                ? 'bg-indigo-500/20 text-indigo-300 border border-indigo-500/30'
                                : 'bg-indigo-50 text-indigo-600 border border-indigo-200'
                            }
                        `}
                    >
                        {tag.name}
                        <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); handleRemove(tag.id); }}
                            className="hover:opacity-70 transition-opacity ml-0.5"
                        >
                            <X size={10} />
                        </button>
                    </span>
                ))}

                {/* Text input */}
                <input
                    ref={inputRef}
                    value={query}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    placeholder={placeholder || (selectedTags.length > 0 ? 'Add more...' : 'Type to search or create tag...')}
                    className={`
                        flex-1 min-w-[100px] text-[11px] bg-transparent outline-none
                        ${isDarkMode ? 'text-slate-200 placeholder-slate-500' : 'text-slate-800 placeholder-slate-400'}
                    `}
                />
            </div>

            {/* Suggestions dropdown */}
            {showDropdown && (
                <div
                    ref={listRef}
                    className={`
                        absolute left-0 right-0 top-full mt-1 z-50 max-h-[160px] overflow-y-auto
                        rounded-lg border shadow-xl py-1
                        ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}
                    `}
                >
                    {suggestions.map((tag, i) => (
                        <button
                            key={tag.id}
                            type="button"
                            onMouseDown={(e) => { e.preventDefault(); handleSelect(tag.id); }}
                            className={`
                                w-full text-left px-3 py-1.5 text-[11px] transition-colors flex items-center gap-2
                                ${i === highlightIndex
                                    ? (isDarkMode ? 'bg-indigo-500/20 text-indigo-300' : 'bg-indigo-50 text-indigo-600')
                                    : (isDarkMode ? 'text-slate-300 hover:bg-slate-700' : 'text-slate-600 hover:bg-slate-50')
                                }
                            `}
                        >
                            <Tag size={10} className="opacity-40" />
                            {tag.name}
                        </button>
                    ))}

                    {/* "Create new" option */}
                    {canCreate && query.trim() && !exactMatch && (
                        <button
                            type="button"
                            disabled={creating}
                            onMouseDown={(e) => { e.preventDefault(); if (!creating) handleCreateAndSelect(); }}
                            className={`
                                w-full text-left px-3 py-1.5 text-[11px] transition-colors flex items-center gap-2 border-t
                                ${creating
                                    ? (isDarkMode ? 'border-slate-700 text-amber-400' : 'border-slate-100 text-amber-600')
                                    : (isDarkMode
                                        ? 'border-slate-700 text-emerald-400 hover:bg-emerald-500/10'
                                        : 'border-slate-100 text-emerald-600 hover:bg-emerald-50')
                                }
                            `}
                        >
                            {creating ? (
                                <>
                                    <span className="text-[9px] opacity-60">＋</span>
                                    Creating<AnimatedDots /> <span className="font-semibold">{query.trim()}</span>
                                </>
                            ) : (
                                <>
                                    <span className="text-[9px] opacity-60">＋</span>
                                    Create <span className="font-semibold">{query.trim()}</span>
                                </>
                            )}
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

/* ============================================================
   NoteListContent — main page
   ============================================================ */
const NoteListContent = ({ isDarkMode, onSelectStoryboard }) => {
    const {
        storyboards, tags, loading, error,
        refetch, addStoryboard, updateStoryboard, deleteStoryboard, togglePinStoryboard,
        addTag,
    } = useStoryboards();

    const [showAddModal, setShowAddModal] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [confirmModal, setConfirmModal] = useState({ isOpen: false });
    const [filterTags, setFilterTags] = useState([]); // active tag filters

    // Add / Edit modal state
    const [formName, setFormName] = useState('');
    const [formTags, setFormTags] = useState([]); // selected tag ids
    const [saving, setSaving] = useState(false);

    // Toggle a tag in the filter
    const toggleFilterTag = useCallback((newIds) => {
        setFilterTags(newIds);
    }, []);

    // Filtered storyboards — show only those that have ALL selected filter tags
    const filteredStoryboards = filterTags.length === 0
        ? storyboards
        : storyboards.filter(sb => {
            const sbTagIds = (sb.tags || []).map(t => Number(t.id));
            return filterTags.every(fId => sbTagIds.includes(fId));
        });

    const openAdd = () => {
        setFormName('');
        setFormTags([]);
        setEditingItem(null);
        setShowAddModal(true);
    };

    const openEdit = (sb) => {
        setFormName(sb.name);
        setFormTags((sb.tags || []).map(t => t.id));
        setEditingItem(sb);
        setShowAddModal(true);
    };

    const handleSubmit = async () => {
        if (!formName.trim()) return;
        setSaving(true);
        if (editingItem) {
            await updateStoryboard({ id: editingItem.id, name: formName.trim(), tag_ids: formTags });
        } else {
            await addStoryboard({ name: formName.trim(), tag_ids: formTags });
        }
        setSaving(false);
        setShowAddModal(false);
    };

    const handleDelete = (sb) => {
        setConfirmModal({
            isOpen: true,
            title: 'Delete Note',
            message: `Delete "${sb.name}" and all its content? This cannot be undone.`,
            onConfirm: async () => {
                await deleteStoryboard(sb.id);
                setConfirmModal(prev => ({ ...prev, isOpen: false }));
            },
            confirmText: 'Delete',
            confirmColor: 'rose',
        });
    };

    return (
        <div className="space-y-4 pb-20 md:pb-0 animate-fade-in">
            {/* Header */}
            <div className="flex justify-between items-center">
                <h2 className="text-lg font-semibold">Note Collection</h2>
                <button
                    onClick={openAdd}
                    className="text-xs bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-1.5 rounded transition-colors shadow-sm"
                >
                    + New Note
                </button>
            </div>

            {/* Tag Filter */}
            {tags.length > 0 && (
                <TagInput
                    tags={tags}
                    selectedTagIds={filterTags}
                    onChange={toggleFilterTag}
                    isDarkMode={isDarkMode}
                    placeholder="Filter by tag..."
                />
            )}

            {/* Error */}
            {error && !loading && (
                <div className={`
                    flex flex-col items-center justify-center py-12 rounded-lg gap-3
                    ${isDarkMode ? 'bg-rose-900/10 border border-rose-800/30' : 'bg-rose-50 border border-rose-200'}
                `}>
                    <p className="text-xs text-rose-500">Failed to load: {error}</p>
                    <button onClick={refetch} className="text-xs bg-rose-500 hover:bg-rose-600 text-white px-3 py-1.5 rounded transition-colors">
                        Retry
                    </button>
                </div>
            )}

            {/* Grid */}
            <div className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {/* Loading Skeletons */}
                {loading && [...Array(8)].map((_, i) => (
                    <div key={i} className={`relative aspect-[3/2] rounded-xl overflow-hidden p-3 flex flex-col justify-end ${isDarkMode ? 'bg-slate-800' : 'bg-slate-50'}`}>
                        <Skeleton className="absolute inset-0 w-full h-full opacity-30" isDarkMode={isDarkMode} />
                        <div className="relative z-10 w-full space-y-2">
                            <div className="flex gap-1">
                                <Skeleton className="h-3 w-10 rounded" isDarkMode={isDarkMode} />
                                <Skeleton className="h-3 w-12 rounded" isDarkMode={isDarkMode} />
                            </div>
                            <Skeleton className="h-4 w-3/4 rounded" isDarkMode={isDarkMode} />
                        </div>
                    </div>
                ))}

                {/* Real Cards */}
                {!loading && !error && filteredStoryboards.map((sb) => (
                    <NoteEntryCard
                        key={sb.id}
                        storyboard={sb}
                        isDarkMode={isDarkMode}
                        onClick={onSelectStoryboard}
                        onEdit={openEdit}
                        onDelete={handleDelete}
                        onTogglePin={togglePinStoryboard}
                    />
                ))}

            </div>

            {/* Empty — no storyboards at all */}
            {!loading && !error && storyboards.length === 0 && (
                <div className={`
                    flex flex-col items-center justify-center py-16 rounded-lg
                    ${isDarkMode ? 'bg-slate-800/30' : 'bg-slate-50'}
                `}>
                    <p className={`text-sm mb-3 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                        No notes yet
                    </p>
                    <button onClick={openAdd} className="text-xs bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-1.5 rounded transition-colors">
                        + Add your first note
                    </button>
                </div>
            )}

            {/* Empty — filter yielded no results */}
            {!loading && !error && storyboards.length > 0 && filteredStoryboards.length === 0 && (
                <div className={`
                    flex flex-col items-center justify-center py-12 rounded-lg
                    ${isDarkMode ? 'bg-slate-800/30' : 'bg-slate-50'}
                `}>
                    <p className={`text-sm mb-3 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                        No notes match the selected tags
                    </p>
                    <button
                        onClick={() => setFilterTags([])}
                        className={`text-xs px-3 py-1.5 rounded transition-colors ${isDarkMode ? 'bg-slate-700 hover:bg-slate-600 text-slate-300' : 'bg-slate-200 hover:bg-slate-300 text-slate-600'}`}
                    >
                        Clear filters
                    </button>
                </div>
            )}

            {/* Add / Edit Modal */}
            {showAddModal && createPortal(
                <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm animate-fade-in" onClick={() => setShowAddModal(false)}>
                    <div
                        onClick={(e) => e.stopPropagation()}
                        className={`
                            w-full max-w-md mx-4 rounded-2xl shadow-2xl p-6 space-y-4 animate-scale-in
                            ${isDarkMode ? 'bg-slate-800 border border-slate-700' : 'bg-white border border-slate-200'}
                        `}
                    >
                        <div className="flex items-center justify-between">
                            <h3 className="text-sm font-semibold">{editingItem ? 'Edit Note' : 'New Note'}</h3>
                            <button onClick={() => setShowAddModal(false)} className="p-1 rounded hover:bg-slate-100/10 transition-colors">
                                <X size={16} />
                            </button>
                        </div>

                        {/* Name */}
                        <div>
                            <label className="text-[10px] font-medium uppercase tracking-wider opacity-60 mb-1 block">Name</label>
                            <input
                                autoFocus
                                value={formName}
                                onChange={(e) => setFormName(e.target.value)}
                                placeholder="Enter note name..."
                                className={`
                                    w-full px-3 py-2 rounded-lg text-sm outline-none border transition-colors
                                    ${isDarkMode
                                        ? 'bg-slate-700/50 border-slate-600 focus:border-indigo-500 text-slate-200'
                                        : 'bg-slate-50 border-slate-200 focus:border-indigo-500 text-slate-800'
                                    }
                                `}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') handleSubmit();
                                }}
                            />
                        </div>

                        {/* Tags — autocomplete combobox */}
                        <div>
                            <label className="text-[10px] font-medium uppercase tracking-wider opacity-60 mb-1 block">Tags</label>
                            <TagInput
                                tags={tags}
                                selectedTagIds={formTags}
                                onChange={setFormTags}
                                onCreateTag={addTag}
                                isDarkMode={isDarkMode}
                            />
                            <p className={`text-[9px] mt-1 ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                                Type to search, ↑↓ to navigate, Enter to select or create new
                            </p>
                        </div>

                        {/* Actions */}
                        <div className="flex justify-end gap-2 pt-2">
                            <button
                                onClick={() => setShowAddModal(false)}
                                className={`
                                    px-4 py-2 text-xs rounded-lg transition-colors
                                    ${isDarkMode ? 'hover:bg-slate-700 text-slate-400' : 'hover:bg-slate-100 text-slate-500'}
                                `}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSubmit}
                                disabled={!formName.trim() || saving}
                                className="px-4 py-2 text-xs rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white transition-colors disabled:opacity-40 shadow-sm"
                            >
                                {saving ? 'Saving...' : (editingItem ? 'Update' : 'Create')}
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {/* Confirmation Modal */}
            <ConfirmationModal
                isOpen={confirmModal.isOpen}
                onClose={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
                onConfirm={confirmModal.onConfirm}
                title={confirmModal.title}
                message={confirmModal.message}
                confirmText={confirmModal.confirmText}
                confirmColor={confirmModal.confirmColor}
                isDarkMode={isDarkMode}
            />
        </div>
    );
};

export default NoteListContent;
